import { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut, Coffee, Play, MapPin, WifiOff, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QKEY = "clockaroo_pending_clock";
const readQ = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q) => localStorage.setItem(QKEY, JSON.stringify(q));
const hms = (s) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), Math.floor(s % 60)].map((n) => String(n).padStart(2, "0")).join(":");
const hm = (s) => `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
const clock = (v) => {
  const d = new Date(v);
  const sameDay = d.toDateString() === new Date().toDateString();
  return d.toLocaleString("en-AU", sameDay ? { hour: "numeric", minute: "2-digit" } : { weekday: "short", hour: "numeric", minute: "2-digit" });
};
const getPos = () => new Promise((res) => {
  if (!navigator.geolocation) { res(null); return; }
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }), () => res(null), { enableHighAccuracy: true, timeout: 10000 });
});
const LABEL = { clock_in: "Clock in", clock_out: "Clock out", break_start: "Break start", break_end: "Break end" };

export const useOnline = () => {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
};

const elapsed = (shift, nowMs) => {
  const open = shift.breaks.find((b) => !b.end);
  const closed = shift.breaks.filter((b) => b.end).reduce((a, b) => a + (new Date(b.end) - new Date(b.start)) / 1000, 0);
  const worked = Math.max(((open ? new Date(open.start).getTime() : nowMs) - new Date(shift.clock_in).getTime()) / 1000 - closed, 0);
  return { worked, open, closed, breakNow: open ? Math.max((nowMs - new Date(open.start).getTime()) / 1000, 0) : 0 };
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-start justify-between gap-3 border-t border-[#E9EEF2] py-2.5 text-sm"><span className="text-slate-500">{label}</span><span className="min-w-0 break-words text-right font-semibold text-[#0B2D5B]" data-testid={testid}>{value}</span></div>
);

const hdr = (biz) => ({ headers: { "X-Business-Id": biz } });

const useClockState = (mid, biz) => {
  const [st, setSt] = useState(null);
  const [skew, setSkew] = useState(0);
  const [queue, setQueue] = useState(() => readQ().filter((q) => q.mid === mid));
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/clock/status", hdr(biz));
      setSt(data);
      if (data.server_time) setSkew(new Date(data.server_time).getTime() - Date.now());
      localStorage.setItem(`clockaroo_last_${mid}`, JSON.stringify(data));
    } catch (e) {
      const c = localStorage.getItem(`clockaroo_last_${mid}`);
      setSt((cur) => cur || (c ? { ...JSON.parse(c), cached: true } : { state: "out", shift: null, cached: true }));
    }
  }, [mid, biz]);
  const flush = useCallback(async () => {
    let q = readQ();
    for (const item of q.filter((x) => x.mid === mid)) {
      try {
        await api.post("/clock/event", item.body, hdr(item.biz || biz));
      } catch (e) {
        if (!e.response) break;
        toast.error(`Your saved ${LABEL[item.body.type].toLowerCase()} couldn't sync: ${formatError(e)}`);
      }
      q = readQ().filter((x) => x.body.client_event_id !== item.body.client_event_id);
      writeQ(q);
    }
    setQueue(readQ().filter((x) => x.mid === mid));
    await load();
  }, [mid, biz, load]);
  useEffect(() => {
    flush();
    const vis = () => { if (document.visibilityState === "visible") flush(); };
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("online", flush);
    window.addEventListener("focus", flush);
    const i = setInterval(flush, 60000);
    return () => { document.removeEventListener("visibilitychange", vis); window.removeEventListener("online", flush); window.removeEventListener("focus", flush); clearInterval(i); };
  }, [flush]);
  const enqueue = (body) => { writeQ([...readQ(), { mid, biz, body }]); setQueue(readQ().filter((x) => x.mid === mid)); };
  return { st, skew, queue, load, enqueue };
};

const StatusBadges = ({ online, cached, pending }) => (
  <div className="flex flex-wrap justify-center gap-2">
    {!online && <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-xs font-bold text-white" data-testid="clock-offline"><WifiOff className="h-3.5 w-3.5" />Offline · showing last confirmed status</span>}
    {online && cached && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="clock-cached">Reconnecting…</span>}
    {pending.map((p) => <span key={p.body.client_event_id} className="inline-flex items-center gap-1 rounded-full bg-[#FF8A00]/15 px-3 py-1 text-xs font-bold text-[#B35F00]" data-testid="clock-pending"><CloudUpload className="h-3.5 w-3.5" />{LABEL[p.body.type]} at {clock(p.body.client_time)} · Pending sync</span>)}
  </div>
);

const RunningLate = () => {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(null);
  const send = async (minutes) => {
    try { await api.post("/clock/running-late", { minutes }); setSent(minutes); setOpen(false); toast.success("Your manager has been told you're running late"); } catch (e) { toast.error(formatError(e)); }
  };
  if (sent) return <p className="mt-3 text-sm font-semibold text-[#B35F00]" data-testid="running-late-sent">Manager notified: about {sent} min late</p>;
  return (
    <div className="mt-3" data-testid="running-late">
      {!open ? <button onClick={() => setOpen(true)} className="h-12 w-full rounded-2xl border border-[#FF8A00] font-semibold text-[#B35F00]" data-testid="running-late-btn">I'm running late</button> : (
        <div className="grid grid-cols-3 gap-2">{[5, 10, 15, 20, 30, 45, 60].map((m) => <button key={m} onClick={() => send(m)} className="h-11 rounded-xl bg-[#FF8A00]/15 font-semibold text-[#B35F00]" data-testid={`running-late-${m}`}>{m} min</button>)}</div>
      )}
    </div>
  );
};

const WorkplacePicker = ({ options, pick, setPick }) => {
  const multiBiz = options.length > 1;
  const all = options.flatMap((o) => o.workplaces.map((w) => ({ ...w, o })));
  const sel = all.find((w) => pick && w.workplace_id === pick.workplace_id);
  if (!all.length) return <p className="mt-4 text-sm text-[#B35F00]" data-testid="clock-no-workplaces">No workplace is set up for you yet. Ask your manager.</p>;
  return (
    <div className="mx-auto mt-4 w-full max-w-sm text-left" data-testid="clock-workplace-picker">
      {all.length > 1 && <p className="mb-2 text-center font-display text-base font-extrabold uppercase tracking-wide text-[#0B2D5B]" data-testid="clock-workplace-prompt">Where are you working today?</p>}
      <div className="grid gap-2">{all.map((w) => {
        const on = pick?.workplace_id === w.workplace_id;
        return (
          <button key={w.workplace_id} type="button" onClick={() => setPick({ business_id: w.o.business_id, membership_id: w.o.membership_id, workplace_id: w.workplace_id })} data-testid={`clock-workplace-option-${w.workplace_id}`}
            className={`flex min-h-[64px] w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${on ? "border-[#00C16B] bg-[#00C16B]/10" : "border-[#E9EEF2] bg-white active:bg-slate-50"}`}>
            <MapPin className={`h-6 w-6 shrink-0 ${on ? "text-[#00A85C]" : "text-slate-400"}`} />
            <span className="min-w-0"><span className="block break-words text-base font-bold text-[#0B2D5B]">{multiBiz && w.name !== w.o.business_name ? w.o.business_name : w.name}</span>
              {multiBiz && w.name !== w.o.business_name && <span className="block break-words text-xs text-slate-500">{w.name}</span>}</span>
          </button>);
      })}</div>
      {!sel && all.length > 1 && <p className="mt-2 text-center text-xs text-[#B35F00]" data-testid="clock-workplace-hint">Pick where you are working, then tap Clock in.</p>}
      {sel && (
        <div className="mt-3 rounded-2xl bg-slate-50 px-4" data-testid="clock-selected-details">
          <Row label="Restaurant" value={sel.o.business_name} testid="clock-sel-business" />
          <Row label="Workplace" value={sel.name} testid="clock-sel-workplace" />
          <Row label="Job role" value={sel.o.job_role || "—"} testid="clock-sel-role" />
          <Row label="Department" value={sel.o.department || "—"} testid="clock-sel-department" />
          <Row label="Location check" value={sel.location_verification ? `Within ${sel.geofence_radius_m} m` : "Not required"} testid="clock-sel-geofence" />
        </div>
      )}
    </div>
  );
};

export const ClockPanel = () => {
  const { membership, business } = useAuth();
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState(null);
  const loadOpts = useCallback(() => api.get("/me/clock-options").then((r) => {
    setOpts(r.data);
    const all = r.data.options.flatMap((o) => o.workplaces.map((w) => ({ business_id: o.business_id, membership_id: o.membership_id, workplace_id: w.workplace_id })));
    if (all.length === 1) setPick(all[0]);
  }).catch(() => setOpts({ options: [], active: null, offline: true })), []);
  useEffect(() => { loadOpts(); }, [loadOpts]);
  if (!opts) return <div className="h-80 animate-pulse rounded-[2rem] bg-white" data-testid="clock-loading" />;
  const t = opts.active || pick || { business_id: business.business_id, membership_id: membership.membership_id };
  return <ClockCore key={t.business_id} biz={t.business_id} mid={t.membership_id} opts={opts} pick={pick} setPick={setPick} onChanged={loadOpts} />;
};

const ClockCore = ({ biz, mid, opts, pick, setPick, onChanged }) => {
  const online = useOnline();
  const { st, skew, queue, load, enqueue } = useClockState(mid, biz);
  const [nowMs, setNowMs] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const bizName = opts.active?.business_name || opts.options.find((o) => o.business_id === biz)?.business_name;
  const wp = pick?.business_id === biz ? pick.workplace_id : "";
  useEffect(() => {
    const i = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const send = async (type) => {
    setBusy(true);
    const body = { type, workplace_id: st?.shift?.workplace_id || wp, client_event_id: crypto.randomUUID(), client_time: new Date(Date.now() + skew).toISOString() };
    const pos = type === "clock_in" || type === "clock_out" ? await getPos() : null;
    try {
      const { data } = await api.post("/clock/event", { ...body, ...(pos || {}) }, hdr(biz));
      const loc = data.event?.location?.status;
      if (data.already) toast.info("You're already clocked in. Showing your active shift.");
      else toast.success(`${LABEL[type]} recorded${loc === "outside" ? " (outside the geofence, flagged for review)" : loc === "inside" ? " (on site)" : ""}`);
      await load();
      if (type === "clock_out" && opts.options.flatMap((o) => o.workplaces).length > 1) setPick(null);
      if (type === "clock_in" || type === "clock_out") onChanged();
    } catch (err) {
      if (!err.response && type !== "clock_in") { enqueue({ ...body, ...(pos || {}) }); toast.warning("You're offline. Saved on this phone; it will sync automatically when you're back online."); }
      else if (!err.response) toast.error("You're offline. Connect to the internet to clock in.");
      else { toast.error(formatError(err)); if (err.response.status === 409) { load(); onChanged(); } }
    } finally { setBusy(false); }
  };
  if (!st) return <div className="h-80 animate-pulse rounded-[2rem] bg-white" data-testid="clock-loading" />;
  const { state, shift } = st;
  const pendingOut = queue.find((q) => q.body.type === "clock_out");
  const e = shift ? elapsed(shift, pendingOut ? new Date(pendingOut.body.client_time).getTime() : nowMs + skew) : null;
  const locked = busy || queue.length > 0;
  const wpName = shift ? shift.workplace_name : null;
  return (
    <div className={`w-full rounded-[2rem] p-5 text-center shadow-sm sm:p-6 ${state === "out" ? "border border-[#E9EEF2] bg-white" : state === "break" ? "border-2 border-[#FF8A00] bg-white" : "border-2 border-[#00C16B] bg-white"}`} data-testid="clock-panel">
      <StatusBadges online={online} cached={st.cached} pending={queue} />
      <p className={`mt-3 font-display text-xl font-extrabold uppercase tracking-wide sm:text-2xl ${state === "in" ? "text-[#00A85C]" : state === "break" ? "text-[#E07800]" : "text-[#0B2D5B]"}`} data-testid="clock-status">
        {pendingOut ? "Clock-out pending" : state === "in" ? "Currently clocked in" : state === "break" ? "On break" : "You're clocked out"}
      </p>
      {state === "out" ? (opts.offline ? <p className="mt-2 text-sm text-slate-500">Reconnecting…</p> : <WorkplacePicker options={opts.options} pick={pick} setPick={setPick} />)
        : <div className="mt-1"><p className="break-words text-base font-bold text-[#0B2D5B]" data-testid="clock-business">{bizName}</p><p className="flex items-center justify-center gap-1 break-words text-sm text-slate-500"><MapPin className="h-4 w-4 shrink-0" /><span data-testid="clock-workplace">{wpName || "Workplace"}</span></p></div>}
      <p className={`font-mono-num mt-4 text-5xl font-bold tabular-nums sm:text-6xl ${state === "break" ? "text-[#E07800]" : "text-[#0B2D5B]"}`} data-testid="clock-timer">{hms(state === "break" ? e.breakNow : e?.worked || 0)}</p>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{state === "break" ? "Break duration" : state === "in" ? "Time worked" : "Ready to start"}</p>
      {shift && (
        <div className="mx-auto mt-4 max-w-sm text-left" data-testid="clock-details">
          <Row label="Clocked in" value={clock(shift.clock_in)} testid="clock-in-time" />
          <Row label="Time worked" value={hm(e.worked)} testid="clock-worked" />
          <Row label="Status" value={state === "break" ? `On break since ${clock(e.open.start)}` : "Still working"} testid="clock-break-status" />
          {e.closed > 0 && <Row label="Breaks taken" value={hm(e.closed)} testid="clock-breaks-taken" />}
        </div>
      )}
      {state === "break" ? (
        <button onClick={() => send("break_end")} disabled={locked} data-testid="clock-break-btn" className="mt-6 flex h-16 w-full items-center justify-center gap-2 rounded-2xl bg-[#FF8A00] text-lg font-bold text-white transition-transform active:scale-95 disabled:opacity-40"><Play className="h-6 w-6" />END BREAK</button>
      ) : (
        <button onClick={() => send(state === "out" ? "clock_in" : "clock_out")} disabled={locked || (state === "out" && (!wp || !online))} data-testid="clock-primary-btn"
          className={`mt-6 flex h-20 w-full items-center justify-center gap-3 rounded-2xl text-xl font-extrabold text-white shadow-lg transition-transform active:scale-95 disabled:opacity-40 ${state === "out" ? "bg-[#00C16B]" : "bg-red-600"}`}>
          {state === "out" ? <><LogIn className="h-7 w-7" />CLOCK IN</> : <><LogOut className="h-7 w-7" />CLOCK OUT</>}
        </button>
      )}
      {state === "in" && <button onClick={() => send("break_start")} disabled={locked} data-testid="clock-start-break-btn" className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#E9EEF2] font-semibold text-[#0B2D5B] disabled:opacity-40"><Coffee className="h-5 w-5" />Start break</button>}
      {state === "break" && <p className="mt-3 text-xs text-slate-500">End your break before clocking out.</p>}
      {state === "out" && online && <RunningLate />}
      {state === "out" && !online && <p className="mt-3 text-sm text-slate-500" data-testid="clock-offline-note">Connect to the internet to clock in.</p>}
      <p className="mt-4 text-xs text-slate-500">Your shift is saved securely. Closing the app or locking your phone won't clock you out. Location is checked only when you clock in or out.</p>
    </div>
  );
};
